import { Component, type ErrorInfo, type ReactNode } from 'react'
import { reportError } from '../lib/errorReporter'

// ErrorBoundary catches render-time throws anywhere in its subtree and
// shows a recoverable fallback instead of letting React unmount the whole
// tree (the white-screen failure mode, finding FE1). It must be a class
// component — React has no hook equivalent for componentDidCatch /
// getDerivedStateFromError.
//
// Two recovery affordances: "Try again" resets the boundary (re-renders
// the children, useful for a transient throw) and "Reload" hard-reloads
// the page (useful when state is wedged). Every caught error is funnelled
// through reportError so it lands in the same structured sink as global
// errors.

interface ErrorBoundaryProps {
  children: ReactNode
  // fallback lets a caller (e.g. the Layout-level boundary) render custom
  // chrome around the error. When omitted the default centered card is used.
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // componentStack is nullable in React 18's types; only forward it when
    // present (exactOptionalPropertyTypes forbids assigning undefined).
    reportError(error, 'react', info.componentStack ? { componentStack: info.componentStack } : undefined)
  }

  reset = (): void => {
    this.setState({ error: null })
  }

  override render(): ReactNode {
    const { error } = this.state
    if (error === null) {
      return this.props.children
    }

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset)
    }

    return <DefaultFallback error={error} reset={this.reset} />
  }
}

// DefaultFallback is the standalone error screen used when no custom
// fallback is supplied (e.g. the root boundary in main.tsx). Plain
// elements only — it must not depend on app context that may itself be
// the thing that threw.
function DefaultFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-4">
      <div
        role="alert"
        className="w-full max-w-md rounded-xl border border-line bg-surface p-6"
      >
        <h1 className="text-lg font-semibold text-cream">Something went wrong</h1>
        <p className="mt-2 text-sm text-cream/70">
          The console hit an unexpected error and couldn&apos;t finish rendering this
          page. You can try again, or reload if the problem persists.
        </p>
        {error.message ? (
          <pre className="mt-3 overflow-x-auto rounded-md bg-surface-2 p-3 text-xs text-cream/80">
            {error.message}
          </pre>
        ) : null}
        <div className="mt-4 flex gap-3">
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-glow-600 px-4 py-2 text-sm font-medium text-ink shadow-[0_0_24px_rgba(232,169,74,0.18)] transition-colors hover:bg-glow-700 hover:shadow-[0_0_32px_rgba(232,169,74,0.32)]"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-line-strong bg-surface px-4 py-2 text-sm font-medium text-cream transition-colors hover:bg-surface-2"
          >
            Reload
          </button>
        </div>
      </div>
    </div>
  )
}
